/**
 * GrammarSpeak Data Service
 * 
 * Unified data service that tries A2A first, falls back to Supabase
 */

import FeatureFlags from '../utils/featureFlags';
import a2aGrammarSpeakService from '../api/services/a2aGrammarSpeakService';
import { getSupabaseClient } from '../utils/supabaseClient';

/**
 * GrammarSpeak Data Service Class
 */
class GrammarSpeakDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    
    // Always log initialization (even in production) for debugging
    console.log('[GrammarSpeakDataService] Initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase,
      featureFlagValue: FeatureFlags.USE_A2A_BACKEND,
      envValue: process.env.REACT_APP_USE_A2A_BACKEND
    });
  }

  /**
   * Try A2A first, fallback to Supabase if enabled
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        console.log(`[GrammarSpeakDataService] ${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        console.log(`[GrammarSpeakDataService] ${methodName}: A2A success`);
        return result;
      } catch (error) {
        const errorDetails = {
          error: error.message,
          name: error.name,
          code: error.code,
          isNetworkError: error.message && (
            error.message.includes('Failed to fetch') ||
            error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('NETWORK_ERROR') ||
            error.message.includes('Invalid URL') ||
            error.message.includes('CORS')
          )
        };
        
        console.error(`[GrammarSpeakDataService] ${methodName}: A2A call failed`, errorDetails);
        
        const isConnectionError = errorDetails.isNetworkError;
        const fallbackAvailable = this.fallbackToSupabase && supabaseFallback;
        
        if (!isConnectionError || !fallbackAvailable) {
          throw error;
        } else {
          console.warn(`[GrammarSpeakDataService] ${methodName}: A2A backend error (${error.message}), using Supabase fallback`);
        }
        
        // Fallback to Supabase if enabled
        if (fallbackAvailable) {
          console.log(`[GrammarSpeakDataService] ${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            console.log(`[GrammarSpeakDataService] ${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            console.error(`[GrammarSpeakDataService] ${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      console.warn(`[GrammarSpeakDataService] ${methodName}: A2A disabled (useA2A=${this.useA2A}), using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  /**
   * Get grammar groups
   * Try A2A, fallback to Supabase
   */
  async getGrammarGroups(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getGrammarGroups(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_grammargroups')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getGrammarGroups'
    );
  }

  /**
   * Get grammar topics
   * Try A2A, fallback to Supabase
   */
  async getGrammarTopics(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getGrammarTopics(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_grammartopics')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getGrammarTopics'
    );
  }

  /**
   * Get grammar units
   * Try A2A, fallback to Supabase
   */
  async getGrammarUnits(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getGrammarUnits(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_grammarbooks').select('*');
        if (params.book_id) {
          query = query.eq('book_id', params.book_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getGrammarUnits'
    );
  }

  /**
   * Get learning paths
   * Try A2A, fallback to Supabase
   */
  async getLearningPaths(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getLearningPaths(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_learningpaths')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getLearningPaths'
    );
  }

  /**
   * Get roleplay scenarios
   * Try A2A, fallback to Supabase
   */
  async getRoleplayScenarios(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getRoleplayScenarios(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_roleplayscenarios')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getRoleplayScenarios'
    );
  }

  /**
   * Get daily challenges
   * Try A2A, fallback to Supabase
   */
  async getDailyChallenges(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getDailyChallenges(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_dailychallenges')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getDailyChallenges'
    );
  }

  /**
   * Complete challenge (A2A only - no Supabase fallback for XP system)
   */
  async completeChallenge(challengeData) {
    if (this.useA2A) {
      try {
        console.log(`[GrammarSpeakDataService] completeChallenge: Using A2A backend`);
        const result = await a2aGrammarSpeakService.completeChallenge(challengeData);
        console.log(`[GrammarSpeakDataService] completeChallenge: A2A success`);
        return result;
      } catch (error) {
        console.error(`[GrammarSpeakDataService] completeChallenge: A2A failed`, error);
        throw error;
      }
    }
    
    throw new Error('completeChallenge: A2A backend is required for challenge completion');
  }

  /**
   * Chat with AI assistant (A2A only)
   */
  async chatAIAssistant(params = {}) {
    if (this.useA2A) {
      try {
        console.log(`[GrammarSpeakDataService] chatAIAssistant: Using A2A backend`);
        const result = await a2aGrammarSpeakService.chatAIAssistant(params);
        console.log(`[GrammarSpeakDataService] chatAIAssistant: A2A success`);
        return result;
      } catch (error) {
        console.error(`[GrammarSpeakDataService] chatAIAssistant: A2A failed`, error);
        throw error;
      }
    }
    
    throw new Error('chatAIAssistant: A2A backend is required for AI chat');
  }

  /**
   * Get tutor voices
   * Try A2A, fallback to Supabase
   */
  async getTutorVoices(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getTutorVoices(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_tutorvoices')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getTutorVoices'
    );
  }

  /**
   * Get bilingual tutor sessions
   * Try A2A, fallback to Supabase
   */
  async getBilingualTutorSessions(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getBilingualTutorSessions(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_bilingualtutorsessions').select('*');
        if (params.user_id) {
          query = query.eq('user_id', params.user_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getBilingualTutorSessions'
    );
  }

  /**
   * Get tone learning units
   * Try A2A, fallback to Supabase
   */
  async getToneLearningUnits(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getToneLearningUnits(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        const { data, error } = await supabase
          .from('eng_tonlearningunits')
          .select('*');
        if (error) throw error;
        return data || [];
      },
      'getToneLearningUnits'
    );
  }

  /**
   * Get achievements
   * Try A2A, fallback to Supabase
   */
  async getAchievements(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getAchievements(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_achievements').select('*');
        if (params.user_id) {
          query = query.eq('user_id', params.user_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getAchievements'
    );
  }

  /**
   * Get learning plan
   * Try A2A, fallback to Supabase
   */
  async getLearningPlan(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getLearningPlan(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_learningplans').select('*');
        if (params.user_id) {
          query = query.eq('user_id', params.user_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getLearningPlan'
    );
  }

  /**
   * Get progress analysis
   * Try A2A, fallback to Supabase
   */
  async getProgressAnalysis(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getProgressAnalysis(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_progressanalysis').select('*');
        if (params.user_id) {
          query = query.eq('user_id', params.user_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getProgressAnalysis'
    );
  }

  /**
   * Get grammar exercises
   * Try A2A, fallback to Supabase
   */
  async getGrammarExercises(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aGrammarSpeakService.getGrammarExercises(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabase client not configured');
        }
        let query = supabase.from('eng_grammarexercises').select('*');
        if (params.topic_id) {
          query = query.eq('topic_id', params.topic_id);
        }
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      'getGrammarExercises'
    );
  }

  /**
   * Submit grammar exercise (A2A only)
   */
  async submitGrammarExercise(exerciseData) {
    if (this.useA2A) {
      try {
        console.log(`[GrammarSpeakDataService] submitGrammarExercise: Using A2A backend`);
        const result = await a2aGrammarSpeakService.submitGrammarExercise(exerciseData);
        console.log(`[GrammarSpeakDataService] submitGrammarExercise: A2A success`);
        return result;
      } catch (error) {
        console.error(`[GrammarSpeakDataService] submitGrammarExercise: A2A failed`, error);
        throw error;
      }
    }
    
    throw new Error('submitGrammarExercise: A2A backend is required for exercise submission');
  }
}

// Create singleton instance
const grammarSpeakDataService = new GrammarSpeakDataService();

export default grammarSpeakDataService;

